import { useEffect } from "react"

/**
 * useVisualViewport Hook
 * 
 * Manages viewport height changes when mobile keyboard appears/disappears.
 * Sets CSS custom properties on document root for use throughout the app.
 * 
 * This hook should be called once at the app root level (App.tsx or main.tsx)
 * to provide global viewport management for all mobile bottom sheets.
 */
export function useVisualViewport() {
  useEffect(() => {
    // Feature detection
    if (typeof window === 'undefined' || !window.visualViewport) {
      // Fallback for browsers without visualViewport API
      let rafId: number | null = null
      
      const updateFallback = () => {
        if (rafId) return // Prevent multiple RAF calls
        
        rafId = requestAnimationFrame(() => {
          const vh = window.innerHeight
          document.documentElement.style.setProperty('--viewport-height', `${vh}px`)
          document.documentElement.style.setProperty('--keyboard-height', '0px')
          rafId = null
        })
      }
      
      updateFallback()
      window.addEventListener('resize', updateFallback)
      
      return () => {
        window.removeEventListener('resize', updateFallback)
        if (rafId) cancelAnimationFrame(rafId)
      }
    }

    // Visual Viewport API implementation
    const visualViewport = window.visualViewport
    let rafId: number | null = null
    let lastHeight = visualViewport.height
    
    const updateViewportHeight = () => {
      if (!visualViewport) return
      
      // Use requestAnimationFrame to batch updates and prevent flickering
      if (rafId) return // Already scheduled
      
      rafId = requestAnimationFrame(() => {
        const viewportHeight = visualViewport.height
        const windowHeight = window.innerHeight
        const keyboardHeight = Math.max(0, windowHeight - viewportHeight)
        
        // Only update if height actually changed (prevent unnecessary repaints)
        if (Math.abs(viewportHeight - lastHeight) > 1) {
          // Set CSS custom properties
          document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`)
          document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`)
          document.documentElement.style.setProperty('--window-height', `${windowHeight}px`)
          
          // Add a data attribute for CSS targeting
          if (keyboardHeight > 100) {
            document.documentElement.setAttribute('data-keyboard-visible', 'true')
          } else {
            document.documentElement.removeAttribute('data-keyboard-visible')
          }
          
          lastHeight = viewportHeight
        }
        
        rafId = null
      })
    }

    // Initial update
    updateViewportHeight()

    // Listen to viewport resize (fires when keyboard opens/closes)
    visualViewport.addEventListener('resize', updateViewportHeight)
    visualViewport.addEventListener('scroll', updateViewportHeight)

    // Cleanup
    return () => {
      visualViewport.removeEventListener('resize', updateViewportHeight)
      visualViewport.removeEventListener('scroll', updateViewportHeight)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [])
}
